import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, MenuItem, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import api from '../../../services/api';
import type { TaxBookEntry, TaxBookParty, TaxBookSummary } from '../../../types/factory';
import { EmptyState, KpiItem, KpiStrip, PageHeader, Section, StatusPill, TONES } from '../../../components/ui/PageKit';
import { errorMessage, fmtMoney } from '../shared';
import { AnimatedNumber } from '../plant/ui';

// Libro IVA ventas y compras (el que lleva el estudio): posición de IVA mes a mes, quién compra y a quién se le
// compra, y el detalle de cada comprobante.

const periodLabel = (p: string) => {
  const d = new Date(`${p}-15T12:00:00`);
  const s = d.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const FactoryTaxBookPage: React.FC = () => {
  const [summary, setSummary] = useState<TaxBookSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [book, setBook] = useState<'Ventas' | 'Compras'>('Ventas');
  const [period, setPeriod] = useState<string>('');
  const [search, setSearch] = useState('');
  const [entries, setEntries] = useState<TaxBookEntry[] | null>(null);

  useEffect(() => {
    api.getTaxBookSummary().then(s => {
      setSummary(s);
      if (s.months.length) setPeriod(s.months[s.months.length - 1].period);
    }).catch(e => setError(errorMessage(e, 'No se pudo traer el libro IVA')));
  }, []);

  useEffect(() => {
    if (!summary?.months.length) return;
    const t = setTimeout(() => {
      setEntries(null);
      api.getTaxBook({ book, period: period || undefined, search: search.trim() || undefined }).then(setEntries).catch(() => setEntries([]));
    }, 250);
    return () => clearTimeout(t);
  }, [book, period, search, summary]);

  const last12 = useMemo(() => (summary?.months ?? []).slice(-12), [summary]);
  const k = useMemo(() => {
    const sum = (f: (m: TaxBookSummary['months'][number]) => number) => last12.reduce((a, m) => a + f(m), 0);
    return { sales: sum(m => m.salesNet), purchases: sum(m => m.purchasesNet), debit: sum(m => m.salesVat), credit: sum(m => m.purchasesVat), position: sum(m => m.vatPosition) };
  }, [last12]);
  const max = Math.max(1, ...last12.flatMap(m => [m.salesNet, m.purchasesNet]));

  if (summary && !summary.months.length) {
    return (
      <>
        <PageHeader title="Libro IVA" />
        <Section><EmptyState title="Todavía no hay libro IVA cargado" hint="Se carga desde la planilla de IVA ventas y compras del estudio." /></Section>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Libro IVA" description="Ventas y compras con factura, mes a mes: cuánto se facturó, cuánto se compró y cuánto IVA queda a pagar o a favor." />
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <KpiStrip>
        <KpiItem label="Ventas netas · 12 meses" value={<AnimatedNumber value={k.sales} prefix="$" />} />
        <KpiItem label="Compras netas · 12 meses" value={<AnimatedNumber value={k.purchases} prefix="$" />} />
        <KpiItem label="IVA débito" value={<AnimatedNumber value={k.debit} prefix="$" />} hint="de las ventas" />
        <KpiItem label="IVA crédito" value={<AnimatedNumber value={k.credit} prefix="$" />} hint="de las compras" />
        <KpiItem label={k.position >= 0 ? 'IVA a pagar · 12 meses' : 'IVA a favor · 12 meses'} value={<AnimatedNumber value={Math.abs(k.position)} prefix="$" />} tone={k.position > 0 ? 'warn' : 'ok'} hint="débito − crédito" />
      </KpiStrip>

      <Section title="Mes a mes" description="Barras: ventas netas (naranja) y compras netas (gris).">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Mes</TableCell>
              <TableCell sx={{ width: '28%' }} />
              <TableCell align="right">Ventas netas</TableCell>
              <TableCell align="right">IVA débito</TableCell>
              <TableCell align="right">Compras netas</TableCell>
              <TableCell align="right">IVA crédito</TableCell>
              <TableCell align="right">Posición</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...(summary?.months ?? [])].reverse().map((m, i) => (
              <TableRow key={m.period} hover selected={m.period === period} onClick={() => setPeriod(m.period)} sx={{ cursor: 'pointer' }}>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{periodLabel(m.period)}</TableCell>
                <TableCell>
                  <Stack spacing={0.4}>
                    {[{ v: m.salesNet, c: '#F0561D' }, { v: m.purchasesNet, c: '#B9BEC7' }].map((b, j) => (
                      <Box key={j} sx={{ height: 6, borderRadius: 3, bgcolor: '#F1F0ED', overflow: 'hidden' }}>
                        <motion.div style={{ height: '100%', background: b.c, borderRadius: 3 }} initial={{ width: 0 }}
                          animate={{ width: `${Math.max(0, (b.v / max) * 100)}%` }} transition={{ duration: 0.7, delay: Math.min(i, 12) * 0.03 }} />
                      </Box>
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="right">{fmtMoney(m.salesNet)}<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{m.salesCount} comprob.</Typography></TableCell>
                <TableCell align="right">{fmtMoney(m.salesVat)}</TableCell>
                <TableCell align="right">{fmtMoney(m.purchasesNet)}<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{m.purchasesCount} comprob.</Typography></TableCell>
                <TableCell align="right">{fmtMoney(m.purchasesVat)}</TableCell>
                <TableCell align="right">
                  <StatusPill tone={m.vatPosition > 0 ? 'warn' : 'ok'}>{m.vatPosition > 0 ? 'a pagar' : 'a favor'} {fmtMoney(Math.abs(m.vatPosition))}</StatusPill>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        <Parties title="Principales clientes" rows={summary?.topCustomers ?? []} />
        <Parties title="Principales proveedores" rows={summary?.topSuppliers ?? []} />
      </Box>

      <Section
        title="Comprobantes"
        actions={(
          <Stack direction="row" spacing={1}>
            <TextField select size="small" value={period} onChange={e => setPeriod(e.target.value)} sx={{ minWidth: 170 }} label="Mes">
              <MenuItem value="">Todos los meses</MenuItem>
              {[...(summary?.months ?? [])].reverse().map(m => <MenuItem key={m.period} value={m.period}>{periodLabel(m.period)}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Buscar" placeholder="Nombre, número o CUIT" value={search} onChange={e => setSearch(e.target.value)} />
          </Stack>
        )}
      >
        <Tabs value={book} onChange={(_, v) => setBook(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="Ventas" label="Ventas" sx={{ textTransform: 'none' }} />
          <Tab value="Compras" label="Compras" sx={{ textTransform: 'none' }} />
        </Tabs>
        {entries === null ? <Box sx={{ height: 80 }} /> : entries.length === 0 ? <EmptyState title="Sin comprobantes con ese filtro" /> : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Comprobante</TableCell>
                <TableCell>{book === 'Ventas' ? 'Cliente' : 'Proveedor'}</TableCell>
                <TableCell>CUIT</TableCell>
                <TableCell align="right">Neto</TableCell>
                <TableCell align="right">IVA</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map(e => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(e.date).toLocaleDateString('es-AR', { timeZone: 'UTC' })}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.type}{e.letter ? ` ${e.letter}` : ''} {e.number}</TableCell>
                  <TableCell>{e.counterparty}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{e.cuit || '—'}</TableCell>
                  <TableCell align="right" sx={{ color: e.net < 0 ? TONES.bad.fg : undefined }}>{fmtMoney(e.net)}</TableCell>
                  <TableCell align="right">{fmtMoney(e.vat)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 650 }}>{fmtMoney(e.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </>
  );
};

const Parties: React.FC<{ title: string; rows: TaxBookParty[] }> = ({ title, rows }) => {
  const max = Math.max(1, ...rows.map(r => r.net));
  return (
    <Section title={title} description="Neto con factura en todo el libro" padded>
      {rows.length === 0 ? <Typography variant="body2" color="text.secondary">Sin datos.</Typography> : (
        <Stack spacing={1.25}>
          {rows.map((r, i) => (
            <Box key={`${r.name}-${r.cuit}`}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>{r.name}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtMoney(r.net)}</Typography>
              </Stack>
              <Box sx={{ mt: 0.4, height: 6, borderRadius: 3, bgcolor: '#F1F0ED', overflow: 'hidden' }}>
                <motion.div style={{ height: '100%', background: '#F0561D', borderRadius: 3 }} initial={{ width: 0 }}
                  animate={{ width: `${(r.net / max) * 100}%` }} transition={{ duration: 0.7, delay: i * 0.04 }} />
              </Box>
              <Typography variant="caption" color="text.secondary">{r.count} comprobante{r.count === 1 ? '' : 's'}{r.cuit ? ` · CUIT ${r.cuit}` : ''}</Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Section>
  );
};

export default FactoryTaxBookPage;
